import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { i18next, dialogs, view, hdrm } from '@yola/ws-sdk';
import ElementSettingsDialog from '../components/element-settings-dialog';
import { APPEARANCE_TAB_ID, DIALOG_HEIGHT, DIALOG_WIDTH } from '../constants';
import getElementSettings from '../accessors/get-element-settings';
import ElementSettingsContext from '../helpers/element-settings-context';
import getInitialState from '../helpers/get-initial-state';
import getObjectDifference from '../helpers/get-object-difference';
import getActionsToApply from '../helpers/get-actions-to-apply';
import prepareElementSettings from '../helpers/prepare-element-settings';
import trackers from '../trackers';
import getAnalyticsTraits from '../helpers/get-analytics-traits';
import getAdjustedOptionsTraits from '../helpers/get-adjusted-options-traits';
import getModifiedOptionsTraits from '../helpers/get-modified-options-traits';

const { UPDATE_LIVE_ONLY, UPDATE_BOTH } = hdrm.constants.updateStrategies;
const {
  trackElementSettingsDialogDisplayed,
  trackElementSettingsDialogCanceled,
  trackElementSettingsDialogSubmitted,
  trackElementSettingsOptionClicked,
  trackElementSettingsSectionToggleClicked,
} = trackers;

function ElementSettingsContainer({
  elements,
  offsetX: initialOffsetX = 0,
  offsetY: initialOffsetY = 0,
}) {
  const captions = useMemo(
    () => ({
      title: i18next.t('Settings'),
      buttons: {
        submit: i18next.t('Save'),
        cancel: i18next.t('Cancel'),
      },
    }),
    []
  );

  const dispatch = useDispatch();
  const changedOptions = useRef({});

  const [offset, setOffset] = useState({ x: initialOffsetX, y: initialOffsetY });

  useEffect(() => {
    if (initialOffsetY || initialOffsetX) return;

    const { offsetX, offsetY } = dialogs.helpers.getPositionByElement(
      elements[0],
      DIALOG_WIDTH,
      DIALOG_HEIGHT
    );

    setOffset({ x: offsetX, y: offsetY });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = useCallback((_, { x, y }) => {
    setOffset({ x, y });
  }, []);

  const matchedSettings = useMemo(() => {
    const elementSettings = getElementSettings();

    return prepareElementSettings(elements, elementSettings);
  }, [elements]);

  const tabs = useMemo(
    () =>
      matchedSettings.reduce(
        (result, setting) => {
          if (setting.tabs) {
            setting.tabs.forEach((tab) => {
              if (tab.id.endsWith(APPEARANCE_TAB_ID)) {
                result.appearance.push(tab);
              } else {
                result.custom.push(tab);
              }
            });
          }

          return result;
        },
        {
          custom: [],
          appearance: [],
        }
      ),
    [matchedSettings]
  );

  const initialTab = tabs.custom.length > 0 ? tabs.custom[0].id : APPEARANCE_TAB_ID;
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    const traits = getAnalyticsTraits(elements, matchedSettings);

    trackElementSettingsDialogDisplayed({
      ...traits,
      tabId: initialTab,
    });
    // eslint-disable-next-line yola/react-hooks/exhaustive-deps
  }, []);

  const initialState = useMemo(() => {
    if (!matchedSettings) return null;

    return getInitialState(matchedSettings);
  }, [matchedSettings]);

  const [state, setState] = useState(initialState);

  const updateState = useCallback(
    (option, batch) => {
      const actions = getActionsToApply(batch, { strategy: UPDATE_LIVE_ONLY });

      dispatch(view.actions.bulkViewActions(actions));

      if (option) {
        Object.keys(batch).forEach((elementId) => {
          if (!changedOptions.current[elementId]) {
            changedOptions.current[elementId] = {};
          }

          Object.keys(batch[elementId]).forEach((attribute) => {
            changedOptions.current[elementId][attribute] = option;
          });
        });
      }

      setState((currentState) => {
        const newState = {
          ...currentState,
        };
        Object.keys(batch).forEach((elementId) => {
          newState[elementId] = {
            ...currentState[elementId],
            ...batch[elementId],
          };
        });

        return newState;
      });
    },
    [dispatch]
  );

  const onSubmit = useCallback(() => {
    const diff = getObjectDifference(initialState, state);
    const traits = getAnalyticsTraits(elements, matchedSettings);
    const adjustedOptionsTraits = getAdjustedOptionsTraits(
      diff,
      changedOptions.current,
      matchedSettings
    );
    const modifiedOptionsTraits = getModifiedOptionsTraits(
      diff,
      changedOptions.current,
      initialState,
      matchedSettings
    );
    trackElementSettingsDialogSubmitted({
      ...traits,
      adjustedOptionsTraits,
      modifiedOptionsTraits,
    });

    if (Object.keys(diff).length > 0) {
      const actions = getActionsToApply(diff, { strategy: UPDATE_BOTH });

      dispatch(view.actions.bulkViewActions(actions));
    }

    dialogs.operations.hide();
  }, [elements, matchedSettings, initialState, state, dispatch]);

  const onCancel = useCallback(() => {
    const diff = getObjectDifference(initialState, state);
    const traits = getAnalyticsTraits(elements, matchedSettings);

    trackElementSettingsDialogCanceled({
      ...traits,
    });

    if (Object.keys(diff).length !== 0) {
      dispatch(view.actions.forceReloadView());
    }

    dialogs.operations.hide();
  }, [elements, matchedSettings, initialState, state, dispatch]);

  const callbacks = useMemo(
    () => ({
      onOptionClicked: ({ optionId, settingId }) => {
        const traits = getAnalyticsTraits(elements, matchedSettings);

        trackElementSettingsOptionClicked({
          ...traits,
          optionId,
          settingId,
        });
      },

      onToggleChange: ({ optionId, settingId, isActive }) => {
        const traits = getAnalyticsTraits(elements, matchedSettings);
        trackElementSettingsSectionToggleClicked({
          ...traits,
          optionId,
          settingId,
          isActive,
        });
      },
    }),
    [elements, matchedSettings]
  );

  if (!matchedSettings) return null;

  return (
    <ElementSettingsContext.Provider
      value={{
        state,
        updateState,
        submit: onSubmit,
        callbacks,
      }}
    >
      <ElementSettingsDialog
        offsetX={offset.x}
        offsetY={offset.y}
        captions={captions}
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        settings={matchedSettings}
        onSubmit={onSubmit}
        onCancel={onCancel}
        onDragEnd={handleDragEnd}
      />
    </ElementSettingsContext.Provider>
  );
}

ElementSettingsContainer.propTypes = {
  elements: PropTypes.array.isRequired,
  offsetX: PropTypes.number,
  offsetY: PropTypes.number,
};

export default ElementSettingsContainer;
